// =============================================================================
// Theme registry — runtime engine
// =============================================================================
//
// A runtime-side store that maps theme id → {@link ThemeBundle}. Hosts
// register every theme they want active at boot:
//
// {@code
//   import { registerTheme } from '@akademi/site-builder-runtime';
//   import { btkTheme } from '@akademi/theme-btk';
//
//   registerTheme('btk', btkTheme);
//   // The runtime's empty baseTheme is auto-registered under 'default'
//   // — tenants on 'gobito-default' resolve to it since gobito-default has no bundle.
// }
//
// Theme bundles ship in {@code @akademi/theme-<name>} packages. The
// runtime stays free of concrete theme imports — adding a new theme
// to a host is one {@code registerTheme} call, never an edit to this
// file or any other platform package.
//
// Three parallel concerns each bundle can override:
//
//   blocks      → editor-droppable blocks (HeroBanner, CourseRow, …).
//                 Driven by the host's defaultBlocks catalog + per-theme
//                 overrides via the resolver in
//                 {@link ./createBlockResolver}.
//
//   system      → product surfaces that aren't editor-driven (categories
//                 index, course detail, …). Hardcoded routes that benefit
//                 from theming but aren't laid out via the designer.
//                 Resolved here by {@link resolveSystem}.
//
//   primitives  → granular UI primitives (CourseCard, ArticleCard,
//                 BlogHero, …) used inside the default system layouts.
//                 Resolved here by {@link resolveSystemPrimitive}.
//
// All three follow the same resolution rules:
//   1. If themes[themeId] has the override, use it.
//   2. Otherwise fall back to themes['default']'s version.
//   3. Otherwise return undefined — the caller decides (block route
//      shows a placeholder; system page falls back to its hardcoded
//      React).

import type { ComponentType, ReactNode } from 'react';
import type { BlockComponent, SystemComponent, ThemeBundle as RuntimeThemeBundle } from './types';
import type { SystemPrimitives, SystemPrimitiveKind } from './SystemPrimitives.types';
import type { HeaderProps } from '../shells/headers';
import type { FooterProps } from '../shells/footers';

// ---- Theme bundle shape ---------------------------------------------------

/**
 * Runtime theme bundle. Themes declare only what they OVERRIDE — the
 * resolver walks fallback chain (theme → default → caller-fallback) so
 * partial bundles are the norm.
 *
 * This is the typed companion of {@link RuntimeThemeBundle} from
 * {@link ./types}: same three top-level keys, with {@link SystemPrimitives}
 * narrowing the primitives map to the registry contract, plus optional
 * {@code header}/{@code footer} chrome overrides for themes with a
 * bespoke header/footer component (their own nav layout, badges, etc.)
 * instead of the runtime's generic chrome. Most themes have neither.
 */
export interface ThemeBundle {
  blocks?: Record<string, BlockComponent>;
  system?: Record<string, SystemComponent>;
  primitives?: SystemPrimitives;
  header?: (style: string, props: HeaderProps) => ReactNode | null;
  footer?: (style: string, props: FooterProps) => ReactNode | null;
}

// ---- Registry store -------------------------------------------------------

const REGISTRY: Map<string, ThemeBundle> = new Map();

// Seed an empty default bundle so resolution never null-pointers
// when a host forgets to register one. Hosts can re-register a
// non-empty default with `registerTheme('default', ...)` later.
REGISTRY.set('default', {});

/**
 * Register a theme bundle under {@code id}. Idempotent — re-registering
 * the same id replaces the previous bundle. Call this at host boot,
 * before the first render that depends on theme resolution.
 *
 * @returns {@code true} on first registration, {@code false} when an
 *   existing bundle was replaced. Useful for tests and dev-mode
 *   double-mount detection.
 */
export function registerTheme(id: string, bundle: ThemeBundle): boolean {
  const isNew = !REGISTRY.has(id);
  REGISTRY.set(id, bundle);
  return isNew;
}

/**
 * Unregister a theme bundle. No-op if the id isn't present. Mainly
 * useful for tests; production hosts register once at boot and never
 * tear down.
 */
export function unregisterTheme(id: string): boolean {
  return REGISTRY.delete(id);
}

/** Get the bundle for {@code id}, or {@code undefined} if unknown. */
export function getTheme(id: string): ThemeBundle | undefined {
  return REGISTRY.get(id);
}

/** Enumerate all registered theme ids (sorted alphabetically for
 *  deterministic admin-side rendering — settings pickers, etc.). */
export function listThemes(): string[] {
  return Array.from(REGISTRY.keys()).sort();
}

/** Test/diagnostic helper — clear all registrations then re-seed the
 *  empty default bundle. */
export function resetThemes(): void {
  REGISTRY.clear();
  REGISTRY.set('default', {});
}

// ---- Resolver functions ---------------------------------------------------

function resolveId(themeId: string | null | undefined): string {
  return themeId ?? 'default';
}

/**
 * Resolve a block component for the active theme. Returns:
 *   1. themes[themeId].blocks[blockType] if present
 *   2. themes['default'].blocks[blockType] if present
 *   3. {@code undefined} — caller falls back to its default blocks map
 *      (typically the host's {@code genericBlocks}).
 */
export function resolveBlock(
  themeId: string | null | undefined,
  blockType: string,
): BlockComponent | undefined {
  const id = resolveId(themeId);
  const themed = REGISTRY.get(id)?.blocks?.[blockType];
  if (themed) return themed;
  return REGISTRY.get('default')?.blocks?.[blockType];
}

/**
 * Resolve a system-page component for the active theme. Returns the
 * theme's override, then the default theme's, then {@code undefined}
 * (caller renders its hardcoded fallback).
 */
export function resolveSystem(
  themeId: string | null | undefined,
  kind: string,
): SystemComponent | undefined {
  const id = resolveId(themeId);
  const themed = REGISTRY.get(id)?.system?.[kind];
  if (themed) return themed;
  return REGISTRY.get('default')?.system?.[kind];
}

/**
 * Resolve a primitive override for the active theme. Returns the
 * theme's override, then the default theme's, then {@code undefined}
 * — callers (system pages) substitute the platform default from
 * {@link ./SystemUI} when this returns {@code undefined}.
 *
 * The companion {@link resolveSystemPrimitiveWithFallback} bakes the
 * platform-default lookup in. Prefer that for new call sites.
 */
export function resolveSystemPrimitive<K extends SystemPrimitiveKind>(
  themeId: string | null | undefined,
  kind: K,
): NonNullable<SystemPrimitives[K]> | undefined {
  const id = resolveId(themeId);
  const themed = REGISTRY.get(id)?.primitives?.[kind];
  if (themed) return themed as NonNullable<SystemPrimitives[K]>;
  const siteDefault = REGISTRY.get('default')?.primitives?.[kind];
  if (siteDefault) return siteDefault as NonNullable<SystemPrimitives[K]>;
  return undefined;
}

/**
 * Resolve a themed header for {@code style} — conventionally the theme
 * id itself (e.g. {@code 'akademi-net'}), matching how
 * {@code SiteShellPresenter} passes {@code config.header.style}
 * straight through as the resolver's {@code style} argument. Returns
 * {@code null} when that theme has no bespoke header, so the caller
 * falls back to the runtime's generic chrome.
 *
 * <p>Drop-in for {@code SiteShellPresenter}'s {@code resolveCustomHeader}
 * prop. Generic replacement for a host hand-chaining
 * {@code resolveXHeader(style, props) ?? resolveYHeader(style, props)}
 * per theme it knows about — register the bundle once via
 * {@link registerTheme} and every caller (production chrome, the
 * admin's shell preview, …) resolves the same theme's header
 * automatically, including themes registered after this call site was
 * written.
 */
export function resolveThemedHeader(style: string, props: HeaderProps): ReactNode | null {
  return REGISTRY.get(style)?.header?.(style, props) ?? null;
}

/** Footer counterpart of {@link resolveThemedHeader}. */
export function resolveThemedFooter(style: string, props: FooterProps): ReactNode | null {
  return REGISTRY.get(style)?.footer?.(style, props) ?? null;
}

/**
 * Adapter for hosts that want to bridge the runtime registry into the
 * existing {@link RuntimeThemeBundle} contract (string-typed primitives)
 * used by {@link createBlockResolver}. Returns the registry as a plain
 * record snapshot — call once at host boot, after every
 * {@link registerTheme} call, then pass into the resolver catalog.
 */
export function getResolverThemes(): Record<string, RuntimeThemeBundle> {
  const out: Record<string, RuntimeThemeBundle> = {};
  for (const [id, bundle] of REGISTRY) {
    out[id] = {
      blocks: bundle.blocks,
      system: bundle.system,
      // SystemPrimitives keys are strings — the registry's runtime
      // contract is open-ended.
      primitives: bundle.primitives as
        | Record<string, ComponentType<Record<string, unknown>>>
        | undefined,
    };
  }
  return out;
}
